import React, { useMemo } from "react";

import Facet from "@/mk2/apps/bazaar/components/Facet";
import { Option } from "@/mk2/apps/bazaar/components/FacetDropdown";
import { OptionKey } from "@/mk2/apps/bazaar/components/FacetDropdownOption";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import {
    BZ2_FACET_LOCATION,
    BZ2_SHOW_PLACES_RADIUS,
    LOCATION_FACET_COUNTY,
} from "@/mk/autogenerated/translation";

export interface OwnProps {
    className?: string;
    isMobile: boolean;
    opened?: boolean;
    dimmed?: boolean;
    locationsOptions: Option[];
    placesOptions: Option[];
    selectedLocations?: OptionKey[];
    selectedPlaces?: OptionKey[];
    placesRadius: boolean;

    onToggle?(facetKey: string);
    onClear?(facetKey: string);
    onChange(facetKey: string, optionKey: OptionKey, checked: boolean);
    onPlacesRadiusChange(enabled: boolean);
}

const emptyArray = [];
const LOCATION_PREFIX = "location_";
const PLACE_PREFIX = "place_";
const PLACES_RADIUS_KEY = "places_radius";
const alwaysDisplayed = [PLACES_RADIUS_KEY];

type Props = OwnProps;

const LocationFacet: React.FunctionComponent<Props> = ({
    className,
    isMobile,
    opened = false,
    dimmed = false,
    locationsOptions = emptyArray,
    placesOptions = emptyArray,
    selectedLocations = emptyArray,
    selectedPlaces = emptyArray,
    onChange,
    onToggle,
    onClear,
    placesRadius,
    onPlacesRadiusChange,
}) => {
    const options: Option[] = useMemo<Option[]>(() => {
        return [
            {
                key: PLACES_RADIUS_KEY,
                label: BZ2_SHOW_PLACES_RADIUS,
            },
            ...locationsOptions.map((o) => ({
                ...o,
                key: `${LOCATION_PREFIX}${o.key}`,
                label: `${o.label} (${LOCATION_FACET_COUNTY})`,
            })),
            ...placesOptions.map((o) => ({
                ...o,
                key: `${PLACE_PREFIX}${o.key}`,
            })),
        ];
    }, [locationsOptions, placesOptions]);

    const selected: string[] = useMemo<string[]>(() => {
        return [
            ...(placesRadius ? [PLACES_RADIUS_KEY] : []),
            ...selectedLocations.map((v) => `${LOCATION_PREFIX}${v}`),
            ...selectedPlaces.map((v) => `${PLACE_PREFIX}${v}`),
        ];
    }, [selectedLocations, selectedPlaces, placesRadius]);

    const onToggleCallback = useEventCallback(() => {
        onToggle("locations");
    }, [onToggle]);

    const onClearCallback = useEventCallback(
        (facetKey: string) => {
            onClear("locations");
            onClear("places");
        },
        [onClear],
    );

    const onChangeCallback = useEventCallback(
        (facetKey: string, optionKey: string, checked: boolean) => {
            if (optionKey === PLACES_RADIUS_KEY) {
                onPlacesRadiusChange(checked);
            } else if (optionKey.startsWith(LOCATION_PREFIX)) {
                onChange(
                    "locations",
                    parseInt(optionKey.substr(LOCATION_PREFIX.length), 10),
                    checked,
                );
            } else if (optionKey.startsWith(PLACE_PREFIX)) {
                onChange("places", parseInt(optionKey.substr(PLACE_PREFIX.length), 10), checked);
            }
        },
        [onChange, onPlacesRadiusChange],
    );

    return (
        <Facet
            facetKey="locations"
            className={className}
            isMobile={isMobile}
            options={options}
            multiselect
            isFilter
            canClear
            search
            opened={opened}
            selected={selected}
            onToggle={onToggleCallback}
            dimmed={dimmed}
            label={BZ2_FACET_LOCATION}
            onChange={onChangeCallback}
            onClear={onClearCallback}
            alwaysDisplayed={alwaysDisplayed}
            settingsOptions={alwaysDisplayed}
        />
    );
};

LocationFacet.displayName = "LocationFacet";

export default React.memo<Props>(LocationFacet);
